import { forwardRef } from 'react'

import { cn } from '@/lib/utils'

// Small auth-scoped primitives in the website schema (white paper, hairline
// rules, 2px-red focus ring, solid-red primary button, square chips). Kept in
// one file since each is tiny and they always travel together.

export function MicroLabel({ children, className }) {

    return (
        <span className={cn('font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-auth-muted', className)}>
            {children}
        </span>
    )
}

export const AuthInput = forwardRef(({ className, error, ...props }, ref) => {

    return (
        <input
            ref={ref}
            className={cn(
                'w-full rounded-[6px] border bg-auth-paper px-3 py-2.5 text-[15px] text-auth-ink',
                'placeholder:text-auth-muted/70',
                'outline-none transition-colors',
                'focus-visible:border-auth-accent focus-visible:ring-2 focus-visible:ring-auth-accent/40 focus-visible:ring-offset-0',
                error ? 'border-auth-accent' : 'border-auth-rule',
                className,
            )}
            {...props}
        />
    )
})
AuthInput.displayName = 'AuthInput'

export function AuthField({ label, error, required, children, className }) {

    return (
        <div className={cn('flex flex-col gap-1.5', className)}>
            {label && (
                <label className="text-[13px] font-medium text-auth-ink">
                    {label}
                    {required && <span className="ml-0.5 text-auth-accent">*</span>}
                </label>
            )}
            {children}
            {error && (
                <span className="text-[12px] text-auth-accent">
                    {error}
                </span>
            )}
        </div>
    )
}

export function AuthChip({ selected, className, type = 'button', ...props }) {

    return (
        <button
            type={type}
            className={cn(
                'inline-flex items-center gap-2 rounded-[3px] border px-3 py-1.5 text-sm transition-colors',
                selected
                    ? 'border-auth-accent text-auth-ink ring-1 ring-auth-accent'
                    : 'border-auth-rule text-auth-muted hover:border-auth-ink hover:text-auth-ink',
                className,
            )}
            {...props}
        />
    )
}

export function AuthButton({ variant = 'primary', className, type = 'button', ...props }) {

    const base = 'inline-flex items-center justify-center gap-2 rounded-[3px] px-5 py-2.5 text-sm font-normal transition-all disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-auth-accent/40'

    const variants = {
        primary: 'bg-auth-accent text-white hover:brightness-105 hover:-translate-y-px hover:shadow-[0_8px_20px_rgba(217,58,58,0.22)]',
        secondary: 'border border-auth-rule bg-transparent text-auth-ink hover:border-auth-ink',
        ink: 'bg-auth-ink text-white hover:bg-auth-accent',
    }

    return (
        <button
            type={type}
            className={cn(base, variants[variant], className)}
            {...props}
        />
    )
}
